import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Fieldset } from "primereact/fieldset";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";

import {
  deleteCustomerById,
  getCustomerById,
} from "../../services/clearing/customers";

import CustomerStations from "./components/CustomerStations";
import CreditNotesGenerationToolbar from "./components/CreditNotesGenerationToolbar/CreditNotesGenerationToolbar";
import DeleteWithPopup from "../components/DeleteWithPopup";
import CustomerChangeHistory from "./CustomerChangeHistory";
import VatIdExclusionDetails from "./components/VatIdExclusionDetails";
import {
  creditNoteVatLabel,
  exclusionStatusLabel,
} from "../../utils/vatIdExclusion";

const DetailField = ({ label, value, fallback = "—" }) => (
  <p className="m-0">
    <strong>{label}:</strong> {value != null && value !== "" ? value : fallback}
  </p>
);

const EmailList = ({ emails }) => {
  if (!emails || emails.length === 0) return <span>—</span>;
  return (
    <ul className="m-0 pl-4">
      {emails.map((email, index) => (
        <li key={index}>{email}</li>
      ))}
    </ul>
  );
};

const CustomerPreviewHeader = ({ customerId }) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-column justify-content-center align-items-center md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
      <div>
        <h2 className="m-0">Customer Details</h2>
        <small className="text-500">ID: {customerId}</small>
      </div>
      <div className="flex flex-column  md:flex-row gap-2 flex-wrap">
        <DeleteWithPopup
          label="Delete Customer"
          onDelete={() => deleteCustomerById(customerId)}
          redirectTo="/customers"
        />

        <Button
          label="Statistics"
          icon="pi pi-chart-bar"
          className="p-button-outlined p-button-primary md:w-12rem"
          onClick={() => navigate(`/customers/${customerId}/statistics`)}
        />

        <Button
          label="Edit"
          icon="pi pi-pencil"
          className="p-button-primary md:w-12rem"
          onClick={() => navigate(`/customers/${customerId}/edit`)}
        />
      </div>
    </div>
  );
};

const CustomerPreview = ({ customer, onExclusionRemoved, toastRef }) => {
  const address = customer.address || {};

  return (
    <section className="grid">
      {/* Contact Info */}
      <div className="col-12 md:col-4">
        <Fieldset legend="Contact Info">
          <div className="grid">
            <div className="col-12">
              <DetailField label="Email" value={customer.email} />
              <DetailField label="Phone" value={customer.phone} />
              <p className="m-0">
                <strong>Additional Recipient Email(s):</strong>{" "}
                <EmailList emails={customer.recipient_emails} />
              </p>
            </div>
          </div>
        </Fieldset>
      </div>

      {/* Bank Details */}
      <div className="col-12 md:col-4">
        <Fieldset legend="Bank Details" className="h-full">
          <div className="grid">
            <div className="col-12">
              <DetailField
                label="Account Holder"
                value={customer.account_holder_name}
              />
              <DetailField label="IBAN" value={customer.iban} />
              <DetailField label="BIC" value={customer.bic} />
            </div>
          </div>
        </Fieldset>
      </div>

      {/* VAT Options */}
      <div className="col-12 md:col-4">
        <Fieldset legend="VAT Options" className="h-full">
          <div className="grid">
            <div className="col-12">
              <DetailField
                label="Customer Type"
                value={customer.business_type}
                fallback="—"
              />

              {/* From the backend's credit-note VAT decision (CLR-273), never from vat_id alone. */}
              <DetailField
                label="Credit notes"
                value={creditNoteVatLabel(customer.credit_note_vat)}
              />

              <DetailField
                label="VAT ID"
                value={customer.vat_id}
                fallback="—"
              />

              <p className="m-0 flex align-items-center gap-2 flex-wrap">
                <strong>Missing VAT ID list:</strong>
                <Tag
                  data-testid="vat-id-exclusion-status"
                  severity={customer.vat_id_exclusion ? "warning" : "info"}
                  value={
                    customer.vat_id_exclusion
                      ? `Excluded — ${exclusionStatusLabel(customer.vat_id_exclusion)}`
                      : exclusionStatusLabel(null)
                  }
                />
              </p>

              {customer.vat_id_exclusion && (
                <VatIdExclusionDetails
                  customerId={customer.id}
                  exclusion={customer.vat_id_exclusion}
                  onUndone={onExclusionRemoved}
                  toastRef={toastRef}
                />
              )}
            </div>
          </div>
        </Fieldset>
      </div>

      {/* Address */}
      <div className="col-12 md:col-4">
        <Fieldset legend="Address" className="h-full">
          <div className="grid">
            <div className="col-12">
              <DetailField label="Company Name" value={address.company_name} />

              <DetailField
                label="Recipient Name"
                value={address.recipient_name}
              />

              <br />

              <DetailField
                label="Street Address"
                value={address.street_address}
              />
              <DetailField label="City" value={address.city} />
              <DetailField label="Postal Code" value={address.postal_code} />
              <DetailField label="Country" value={address.country} />
            </div>
          </div>
        </Fieldset>
      </div>

      {/* Invoice Preferences */}
      <div className="col-12 md:col-4">
        <Fieldset legend="Invoice Preferences" className="h-full">
          <div className="grid">
            <div className="col-12">
              <DetailField
                label="Invoice Language"
                value={customer.invoice_language}
              />

              <DetailField
                label="Detailed Invoices"
                value={customer.detailed_invoice ? "YES" : "NO"}
              />

              <DetailField
                label="Attach CSV to Credit Note"
                value={customer.attach_csv_to_credit_note ? "YES" : "NO"}
              />
            </div>
          </div>
        </Fieldset>
      </div>


      {/* Additional Info */}
      <div className="col-12 md:col-4">
        <Fieldset legend="Additional Info" className="h-full">
          <div className="grid">
            <div className="col-12">
              <DetailField label="Industry" value={customer.industry} />
            </div>
          </div>
        </Fieldset>
      </div>
    </section>
  );
};

const CustomerDetails = () => {
  const { id: customerId } = useParams();
  const toast = useRef(null);
  const [customer, setCustomer] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Resolves once the page shows the stored customer; rejects if it could not be read.
  const reloadCustomer = useCallback(
    () => getCustomerById(customerId).then(setCustomer),
    [customerId],
  );

  useEffect(() => {
    if (customerId) {
      reloadCustomer().catch((err) => console.error("Failed to load customer:", err));
    }
  }, [customerId, reloadCustomer]);

  if (!customer) {
    return <div className="p-4 text-center">Loading customer data...</div>;
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <Toast ref={toast} />

      <CustomerPreviewHeader customerId={customer?.id} />

      <Divider className="my-4" />

      {/* Sectioned Details */}
      <CustomerPreview
        customer={customer}
        toastRef={toast}
        // Re-fetch rather than patch locally: removing the exclusion also changes the
        // credit-note VAT, which only the backend decides.
        onExclusionRemoved={reloadCustomer}
      />

      {/* Additional Sections */}
      <CustomerStations customerId={customerId} />
      <CreditNotesGenerationToolbar customerId={customerId} customerEmail={customer?.email} />

      {/* Change History */}
      <Card className="mt-4 shadow-2">
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="m-0">Change History</h3>
          <Button
            label={showHistory ? "Hide" : "Show"}
            icon={showHistory ? "pi pi-eye-slash" : "pi pi-eye"}
            className="p-button-outlined p-button-sm"
            onClick={() => setShowHistory((prev) => !prev)}
          />
        </div>
        {showHistory && <CustomerChangeHistory customerId={customerId} />}
      </Card>
    </div>
  );
};

export default CustomerDetails;
